import { useEffect, useRef, useCallback, useState } from 'react';

const TURNSTILE_SITE_KEY = '0x4AAAAAADhTbE_Seq3HdMf-';

// Check if we're on a preview/development environment
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('preview.emergentagent.com') || 
         hostname.includes('preview.emergentcf.cloud') ||
         hostname.includes('.emergentcf.cloud') ||
         hostname === 'localhost' || 
         hostname === '127.0.0.1';
};

/**
 * Cloudflare Turnstile CAPTCHA Component
 * 
 * Fixed to prevent endless loading loop by:
 * 1. Using refs to store callbacks instead of dependencies
 * 2. Only initializing once on mount
 * 3. Properly handling script loading state
 * 
 * @param {Object} props
 * @param {Function} props.onVerify - Callback when verification succeeds, receives token
 * @param {Function} props.onError - Callback when verification fails
 * @param {Function} props.onExpire - Callback when token expires
 * @param {string} props.theme - 'light', 'dark', or 'auto'
 * @param {string} props.size - 'normal' or 'compact'
 */
const Turnstile = ({ 
  onVerify, 
  onError, 
  onExpire,
  theme = 'light',
  size = 'normal'
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const initializedRef = useRef(false);
  const [isPreview, setIsPreview] = useState(false);
  
  // Store callbacks in refs to avoid re-initialization when they change
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  
  // Check for preview environment on mount
  useEffect(() => {
    const preview = isPreviewEnvironment();
    setIsPreview(preview);
    
    // Auto-verify with a bypass token on preview environments
    if (preview && onVerifyRef.current) {
      // Small delay to simulate widget loading
      const timer = setTimeout(() => {
        onVerifyRef.current('PREVIEW_BYPASS_TOKEN');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Update refs when callbacks change (but don't trigger re-render)
  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);
  
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Stable callback handlers that use refs
  const handleSuccess = useCallback((token) => {
    if (onVerifyRef.current) {
      onVerifyRef.current(token);
    }
  }, []);

  const handleError = useCallback(() => {
    if (onErrorRef.current) {
      onErrorRef.current();
    }
  }, []);

  const handleExpire = useCallback(() => {
    if (onExpireRef.current) {
      onExpireRef.current();
    }
  }, []);

  useEffect(() => {
    // Skip Turnstile initialization on preview environments
    if (isPreviewEnvironment()) {
      return;
    }
    
    // Prevent multiple initializations
    if (initializedRef.current) {
      return;
    }

    // Load the Turnstile script if not already loaded
    const loadScript = () => {
      return new Promise((resolve, reject) => {
        if (window.turnstile) {
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[src*="turnstile"]');
        if (existingScript) {
          // Script exists, wait for it to load
          if (window.turnstile) {
            resolve();
            return;
          }
          existingScript.addEventListener('load', resolve);
          existingScript.addEventListener('error', reject);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const initTurnstile = async () => {
      try {
        await loadScript();

        // Wait for turnstile to be available
        let attempts = 0;
        while (!window.turnstile && attempts < 50) {
          await new Promise(r => setTimeout(r, 100));
          attempts++;
        }

        if (!window.turnstile) {
          console.error('Turnstile failed to load');
          return;
        }

        // Only render if not already initialized and container exists
        if (!initializedRef.current && containerRef.current) {
          initializedRef.current = true;
          
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: handleSuccess,
            'error-callback': handleError,
            'expired-callback': handleExpire,
            theme: theme,
            size: size
          });
        }
      } catch (error) {
        console.error('Failed to initialize Turnstile:', error);
      }
    };

    initTurnstile();

    return () => {
      // Cleanup on unmount
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be removed - safe to ignore during unmount
          console.debug('Turnstile unmount cleanup:', e?.message);
        }
        widgetIdRef.current = null;
        initializedRef.current = false;
      }
    };
  // Only run on mount - theme and size are initial config
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Method to reset the widget (can be called externally if needed)
  const reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="turnstile-container"
      data-testid="turnstile-widget"
    >
      {isPreview && (
        <div className="flex items-center justify-center p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Preview Mode - CAPTCHA Bypassed
        </div>
      )}
    </div>
  );
};

export default Turnstile;
